import {
  readDtoolsDisplaySettings,
  resolveDtoolsPaymentTerms,
  type DtoolsPortalPaymentTerm,
} from "./dtoolsDisplaySettings";
import { resolveCustomerFacingPaymentTerms } from "./proposalCustomerPaymentTerms";
import {
  parseDtoolsLineCatalogMeta,
  resolveLineDisplayDescription,
  resolveLineDisplayImage,
} from "./dtoolsCatalogueMatch";
import {
  buildReviewHierarchy,
  groupIdenticalProductsInReviewHierarchy,
  type DtoolsStagingLine,
} from "./dtoolsJsonStaging";
import { computeStagingLinesCustomerTotal } from "./dtoolsStagingReconciliation";
import { applyComposedScopeToStagingLines } from "./variationOrderApplyToLiveView";
import type { ComposedProjectScope } from "./variationOrderCurrentScope";
import type { DtoolsItemBucket } from "./dtoolsSiJsonImport";
import {
  DTOOLS_JSON_IMPORT_VERSION,
  DTOOLS_JSON_SOURCE,
  type DtoolsJsonParseResult,
  type DtoolsSiPaymentTerm,
} from "./dtoolsSiJsonImport";
import type { DtoolsReviewFooter, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import {
  readDtoolsSystemContentMap,
} from "./dtoolsProposalContent";
import {
  readDtoolsProposalPresentation,
  type DtoolsProposalPresentation,
  type ProposalPresentationHeaderInput,
} from "./dtoolsProposalPresentation";
import { applyHierarchyOverridesToLines, elevateServiceLinesToRoomPrimaries, sanitizeStagingLineFields, readDtoolsHierarchyOverrides } from "./dtoolsHierarchyOverrides";
import {
  resolveDtoolsCustomerTabGate,
  type DtoolsSystemClassificationsMap,
  type DtoolsTabModelVersion,
} from "./dtoolsSystemClassification";
import { isDtoolsJsonProposalSourceValue } from "./proposalSources";
import { PROPOSAL_SUMMARY_LINE_DESCRIPTION } from "./proposalUploadDoctrine";
import { isDtoolsJsonProposalSourceValue } from "./proposalSources";
import { withInternalSystemsHiddenByDefault } from "./dtoolsOptionVisibility";
import { fallbackProductDisplayName } from "./productDisplayName";

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

export const DTOOLS_JSON_UPLOAD_SANDBOX_PATH = "/sales/sandbox/dtools-json-upload";
export const DTOOLS_JSON_UPLOAD_PRODUCTION_PATH = "/sales/dtools-json-upload";

/** Preserves sandbox vs production URL prefix through upload → setup → review. */
export function resolveDtoolsJsonUploadBasePath(pathname: string): string {
  const path = (pathname.split("?")[0] ?? "").split("#")[0] ?? "";
  if (/\/sales\/dtools-json-upload(?:\/|$)/i.test(path)) {
    return DTOOLS_JSON_UPLOAD_PRODUCTION_PATH;
  }
  return DTOOLS_JSON_UPLOAD_SANDBOX_PATH;
}

export type ProposalContentLayout = "standard" | "dtools_beautified";

export type ProposalDbItemForDtoolsView = {
  id: number;
  name?: string | null;
  brand?: string | null;
  model?: string | null;
  displayName?: string | null;
  displayNameManuallyEdited?: boolean | null;
  category?: string | null;
  rawText?: string | null;
  descriptionSnapshot?: string | null;
  imageUrlSnapshot?: string | null;
  resolvedImageUrl?: string | null;
  quantity?: number | null;
  unitPriceSnapshot?: number | null;
  lineTotal?: number | null;
  sortOrder?: number | null;
  cleoDescription?: string | null;
  customerExplanation?: string | null;
  pricingMethod?: string | null;
  percentageOfEquipment?: string | number | null;
};

export type ProposalHeaderForDtoolsView = {
  proposalSource?: string | null;
  proposalName?: string | null;
  proposalRef?: string | null;
  totalAmount?: number | string | null;
  createdAt?: string | Date | null;
  linkBExpiresAt?: string | Date | null;
  paymentTermsJson?: unknown;
  parsedMetadataJson?: unknown;
  customerNameSnapshot?: string | null;
  projectNumber?: string | null;
};

type DtoolsItemRawMeta = {
  source?: string;
  dtoolsItemId?: string;
  catalogueKey?: string;
  catalogId?: string | null;
  componentId?: string | null;
  bucket?: DtoolsItemBucket;
  systemId?: string | null;
  systemName?: string | null;
  locationId?: string | null;
  roomName?: string | null;
  locationName?: string | null;
  locationFullName?: string | null;
  parentLocationId?: string | null;
  parentId?: string | null;
  isAccessory?: boolean;
  originalDtoolsDescription?: string;
  polishedDescription?: string;
  mergedSources?: DtoolsStagingLine["mergedSources"];
  catalogProductId?: number | null;
  catalogMatchConfidence?: string | null;
  projectCatalogueId?: number | null;
  proposalImageOverride?: string | null;
  proposalDescriptionOverride?: string | null;
  /** Product-only base unit price (pre-markup), when known. */
  baseUnitPrice?: number | null;
  /** Retail or project catalogue image URL captured at match time. */
  catalogImageUrl?: string | null;
  imageUrl?: string | null;
};

type HierarchySnapshotLine = {
  id?: string;
  dtoolsItemId?: string;
  bucket?: DtoolsItemBucket;
  systemId?: string | null;
  systemName?: string | null;
  locationId?: string | null;
  roomName?: string | null;
  locationName?: string | null;
  locationFullName?: string | null;
  parentLocationId?: string | null;
  parentId?: string | null;
  isAccessory?: boolean;
  catalogueKey?: string;
  catalogId?: string | null;
  componentId?: string | null;
  sortOrder?: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseDtoolsItemRawText(rawText: string | null | undefined): DtoolsItemRawMeta {
  const text = String(rawText ?? "").trim();
  if (!text) return {};
  try {
    const parsed = JSON.parse(text) as unknown;
    const record = asRecord(parsed);
    if (!record || record.source !== DTOOLS_JSON_SOURCE) return {};
    return record as DtoolsItemRawMeta;
  } catch {
    return {};
  }
}

function snapshotLineIndex(snapshot: Record<string, unknown> | null): Map<string, HierarchySnapshotLine> {
  const map = new Map<string, HierarchySnapshotLine>();
  if (!snapshot) return map;
  const lines = snapshot.lines;
  if (!Array.isArray(lines)) return map;
  for (const entry of lines) {
    const row = asRecord(entry);
    if (!row) continue;
    const id = String(row.id ?? row.dtoolsItemId ?? "").trim();
    if (!id) continue;
    map.set(id, row as HierarchySnapshotLine);
    const dtoolsItemId = String(row.dtoolsItemId ?? "").trim();
    if (dtoolsItemId && dtoolsItemId !== id) {
      map.set(dtoolsItemId, row as HierarchySnapshotLine);
    }
  }
  return map;
}

function isPlaceholderSourceDescription(value: string | null | undefined): boolean {
  const text = String(value ?? "")
    .trim()
    .toLowerCase();
  return !text || text === "line item";
}

function resolvePersistedSourceDtoolsDescription(
  meta: DtoolsItemRawMeta,
  item: ProposalDbItemForDtoolsView,
): { text: string | null; reliable: boolean } {
  const fromOriginal = String(meta.originalDtoolsDescription ?? "").trim();
  if (fromOriginal && !isPlaceholderSourceDescription(fromOriginal)) {
    return { text: fromOriginal, reliable: true };
  }
  const fromMerged = String(meta.mergedSources?.[0]?.dtoolsDescription ?? "").trim();
  if (fromMerged && !isPlaceholderSourceDescription(fromMerged)) {
    return { text: fromMerged, reliable: true };
  }
  const snapshot = String(item.descriptionSnapshot ?? "").trim();
  if (
    snapshot &&
    !isPlaceholderSourceDescription(snapshot) &&
    fromOriginal &&
    normComparableText(snapshot) === normComparableText(fromOriginal)
  ) {
    return { text: snapshot, reliable: true };
  }
  return { text: null, reliable: false };
}

function normComparableText(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function proposalDbItemToStagingLine(
  item: ProposalDbItemForDtoolsView,
  snapshotLine?: HierarchySnapshotLine,
): DtoolsStagingLine {
  const meta = parseDtoolsItemRawText(item.rawText);
  const catalogMeta = parseDtoolsLineCatalogMeta(meta);
  const qty = Math.max(1, Math.round(Number(item.quantity) || 1));
  const lineTotal = Math.max(0, Number(item.lineTotal) || 0);
  const unitPrice = Math.max(0, Number(item.unitPriceSnapshot) || (qty > 0 ? lineTotal / qty : 0));
  const id =
    String(meta.dtoolsItemId ?? snapshotLine?.id ?? snapshotLine?.dtoolsItemId ?? item.id).trim() ||
    String(item.id);
  const sourceDescription = resolvePersistedSourceDtoolsDescription(meta, item);

  return {
    id,
    dtoolsItemId: String(meta.dtoolsItemId ?? snapshotLine?.dtoolsItemId ?? id),
    manufacturer: item.brand ?? null,
    model: item.model ?? null,
    displayName:
      item.displayName?.trim() ||
      fallbackProductDisplayName({ brand: item.brand, model: item.model, name: item.name }),
    displayNameManuallyEdited: item.displayNameManuallyEdited === true,
    category: item.category ?? null,
    subcategory: null,
    dtoolsDescription:
      String(meta.originalDtoolsDescription ?? item.descriptionSnapshot ?? item.name ?? "").trim() || "Line item",
    polishedDescription:
      resolveLineDisplayDescription(
        String(meta.polishedDescription ?? item.descriptionSnapshot ?? meta.originalDtoolsDescription ?? item.name ?? ""),
        catalogMeta,
      ) || "Line item",
    sourceDtoolsDescription: sourceDescription.text,
    sourceDtoolsDescriptionReliable: sourceDescription.reliable,
    quantity: qty,
    baseUnitPrice: (() => {
      const fromMeta = Number(meta.baseUnitPrice);
      if (Number.isFinite(fromMeta) && fromMeta >= 0) return fromMeta;
      return unitPrice;
    })(),
    unitPrice,
    lineTotal,
    systemId: meta.systemId ?? snapshotLine?.systemId ?? null,
    systemName: meta.systemName ?? snapshotLine?.systemName ?? null,
    locationId: meta.locationId ?? snapshotLine?.locationId ?? null,
    roomName: meta.roomName ?? snapshotLine?.roomName ?? null,
    locationName: meta.locationName ?? snapshotLine?.locationName ?? null,
    locationFullName: meta.locationFullName ?? snapshotLine?.locationFullName ?? null,
    parentLocationId: meta.parentLocationId ?? snapshotLine?.parentLocationId ?? null,
    parentId: meta.parentId ?? snapshotLine?.parentId ?? null,
    sortOrder: item.sortOrder ?? snapshotLine?.sortOrder ?? 0,
    bucket: meta.bucket ?? snapshotLine?.bucket ?? "commercial",
    imageUrl:
      resolveLineDisplayImage(
        item.imageUrlSnapshot ?? item.resolvedImageUrl ?? meta.imageUrl,
        catalogMeta,
        meta.catalogImageUrl,
      ) || null,
    projectCatalogueId: (() => {
      const fromMeta = Number(meta.projectCatalogueId);
      if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;
      return null;
    })(),
    catalogueKey: meta.catalogueKey ?? snapshotLine?.catalogueKey ?? "",
    catalogId: meta.catalogId ?? snapshotLine?.catalogId ?? null,
    componentId: meta.componentId ?? snapshotLine?.componentId ?? null,
    isAccessory: Boolean(meta.isAccessory ?? snapshotLine?.isAccessory),
    mergedSources: meta.mergedSources,
    catalogProductId: catalogMeta.catalogProductId ?? null,
    catalogMatchConfidence: catalogMeta.catalogMatchConfidence ?? null,
    proposalImageOverride: catalogMeta.proposalImageOverride ?? null,
    proposalDescriptionOverride: catalogMeta.proposalDescriptionOverride ?? null,
    cleoDescription: String(item.cleoDescription ?? "").trim() || null,
    customerExplanation: String(item.customerExplanation ?? "").trim() || null,
    pricingMethod: (() => {
      const method = String(item.pricingMethod ?? "").toLowerCase();
      if (method === "percentage_of_equipment") return "percentage_of_equipment";
      if (method === "fixed") return "fixed";
      return null;
    })(),
    percentageOfEquipment: (() => {
      if (item.percentageOfEquipment == null || item.percentageOfEquipment === "") return null;
      const n = Number(item.percentageOfEquipment);
      return Number.isFinite(n) ? n : null;
    })(),
  };
}

export function isDtoolsPersistedProposalItem(item: ProposalDbItemForDtoolsView): boolean {
  const meta = parseDtoolsItemRawText(item.rawText);
  return meta.source === DTOOLS_JSON_SOURCE;
}

export function isProposalSummaryWrapperItem(item: ProposalDbItemForDtoolsView): boolean {
  const desc = String(item.descriptionSnapshot ?? item.name ?? "").trim();
  if (desc === PROPOSAL_SUMMARY_LINE_DESCRIPTION) return true;
  const raw = String(item.rawText ?? "").trim();
  if (!raw) return false;
  if (raw === PROPOSAL_SUMMARY_LINE_DESCRIPTION) return true;
  return raw.toLowerCase().includes(PROPOSAL_SUMMARY_LINE_DESCRIPTION.toLowerCase());
}

export function hasDtoolsPersistedProposalItems(items: ProposalDbItemForDtoolsView[]): boolean {
  return items.some((item) => isDtoolsPersistedProposalItem(item) && !isProposalSummaryWrapperItem(item));
}

function shouldIncludeProposalItemForDtoolsView(
  item: ProposalDbItemForDtoolsView,
  byKey: Map<string, HierarchySnapshotLine>,
  hasSnapshot: boolean,
): boolean {
  if (isProposalSummaryWrapperItem(item)) return false;
  if (isDtoolsPersistedProposalItem(item)) return true;
  if (!hasSnapshot) return false;
  const rawMeta = parseDtoolsItemRawText(item.rawText);
  const dtoolsItemId = String(rawMeta.dtoolsItemId ?? "").trim();
  return byKey.has(dtoolsItemId) || byKey.has(String(item.id));
}

export function buildDtoolsStagingLinesFromProposalItems(
  items: ProposalDbItemForDtoolsView[],
  parsedMetadataJson?: unknown,
): DtoolsStagingLine[] {
  const meta = asRecord(parsedMetadataJson);
  const snapshot = asRecord(meta?.dtoolsHierarchySnapshot);
  const byKey = snapshotLineIndex(snapshot);
  const hasSnapshot = hasDtoolsHierarchySnapshot(parsedMetadataJson);
  return [...items]
    .filter((item) => shouldIncludeProposalItemForDtoolsView(item, byKey, hasSnapshot))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id)
    .map((item) => {
      const rawMeta = parseDtoolsItemRawText(item.rawText);
      const snapshotLine =
        byKey.get(String(rawMeta.dtoolsItemId ?? "").trim()) ??
        byKey.get(String(item.id));
      return proposalDbItemToStagingLine(item, snapshotLine);
    });
}

function mapPortalPaymentTerms(terms: DtoolsPortalPaymentTerm[]): DtoolsSiPaymentTerm[] {
  return terms.map((term) => ({
    payment: term.stage,
    useFixedAmount: false,
    billingPercentage: term.percentage,
    billingAmount: null,
    billingDate: null,
    coNumber: null,
  }));
}

function paymentTermsFromHeader(header: ProposalHeaderForDtoolsView): DtoolsSiPaymentTerm[] {
  const resolved = resolveDtoolsPaymentTerms(header.paymentTermsJson);
  return mapPortalPaymentTerms(resolved);
}

export function buildDtoolsParseResultFromProposalHeader(
  header: ProposalHeaderForDtoolsView,
): Pick<DtoolsJsonParseResult, "project" | "paymentTerms" | "reconciliation"> {
  const meta = asRecord(header.parsedMetadataJson);
  const totalsCheck = asRecord(meta?.totalsCheck);
  const headerTotal = Number(header.totalAmount ?? totalsCheck?.headerTotal ?? 0) || 0;
  const itemsTotal = Number(totalsCheck?.itemsTotal ?? headerTotal) || 0;
  const absoluteDelta = Number(totalsCheck?.absoluteDelta ?? Math.abs(itemsTotal - headerTotal)) || 0;
  const relativeDelta = Number(totalsCheck?.relativeDelta ?? (headerTotal > 0 ? absoluteDelta / headerTotal : 0)) || 0;
  const severeMismatch = Boolean(totalsCheck?.severeMismatch);
  const createdOn = header.createdAt ? String(header.createdAt) : null;

  return {
    project: {
      dtoolsProjectId: null,
      proposalName: String(header.proposalName ?? meta?.proposalName ?? "Proposal").trim() || "Proposal",
      proposalRef: String(header.proposalRef ?? meta?.projectNumber ?? "").trim() || null,
      revision: null,
      progress: null,
      currency: "SGD",
      currencyNormalizedFromUsd: false,
      projectTotal: headerTotal,
      priceWithoutTax: null,
      tax: null,
      scopeOfWork: null,
      notes: null,
      createdOn,
      updatedOn: createdOn,
      salesperson: null,
      projectManager: null,
    },
    paymentTerms: paymentTermsFromHeader(header),
    reconciliation: {
      currency: "SGD",
      headerTotal,
      headerSubtotal: null,
      headerTax: null,
      linesCustomerTotal: itemsTotal,
      linesOptionalTotal: 0,
      linesNonBillableTotal: 0,
      deltaVsHeaderTotal: absoluteDelta,
      deltaVsSubtotal: null,
      relativeDeltaVsTotal: relativeDelta,
      severeMismatch,
      reconciled: !severeMismatch,
    },
  };
}

export function hasDtoolsHierarchySnapshot(parsedMetadataJson?: unknown): boolean {
  const meta = asRecord(parsedMetadataJson);
  const snapshot = asRecord(meta?.dtoolsHierarchySnapshot);
  return Boolean(snapshot && Array.isArray(snapshot.lines) && snapshot.lines.length > 0);
}

export function hasDtoolsCommercialParseAudit(parsedMetadataJson?: unknown): boolean {
  const meta = asRecord(parsedMetadataJson);
  const audit = meta?.commercialParseAudit;
  if (!Array.isArray(audit)) return false;
  return audit.some((entry) => {
    const row = asRecord(entry);
    if (!row) return false;
    if (row.dtoolsItemSummary && typeof row.dtoolsItemSummary === "object") return true;
    return isDtoolsPersistedProposalItem({ rawText: String(row.rawText ?? "") });
  });
}

/** Rebuild D-Tools line items from header metadata when proposal_items were collapsed to summary-only. */
export function recoverDtoolsProposalDbItemsFromParsedMetadata(
  parsedMetadataJson: unknown,
): ProposalDbItemForDtoolsView[] {
  const meta = asRecord(parsedMetadataJson);
  const audit = meta?.commercialParseAudit;
  if (!Array.isArray(audit)) return [];

  const items: ProposalDbItemForDtoolsView[] = [];
  let sortOrder = 0;
  for (const entry of audit) {
    const row = asRecord(entry);
    if (!row) continue;

    const summary = asRecord(row.dtoolsItemSummary);
    const rawTextField = String(row.rawText ?? "").trim();
    let rawText = rawTextField;

    if (!isDtoolsPersistedProposalItem({ rawText }) && summary) {
      rawText = JSON.stringify({
        source: DTOOLS_JSON_SOURCE,
        dtoolsItemId: summary.dtoolsItemId ?? null,
        catalogueKey: summary.catalogueKey ?? "",
        catalogId: summary.catalogId ?? null,
        componentId: summary.componentId ?? null,
        bucket: summary.bucket ?? "commercial",
        systemId: summary.systemId ?? null,
        systemName: summary.systemName ?? null,
        locationId: summary.locationId ?? null,
        roomName: summary.roomName ?? null,
        locationName: summary.locationName ?? null,
        locationFullName: summary.locationFullName ?? null,
        parentLocationId: summary.parentLocationId ?? null,
        parentId: summary.parentId ?? null,
        isAccessory: Boolean(summary.isAccessory),
        originalDtoolsDescription: summary.originalDtoolsDescription ?? "",
        polishedDescription: summary.polishedDescription ?? "",
        ...parseDtoolsLineCatalogMeta(summary),
      });
    }

    if (!isDtoolsPersistedProposalItem({ rawText })) continue;

    items.push({
      id: -(sortOrder + 1),
      name: String(row.name ?? row.description ?? "Line item"),
      brand: (row.brand as string | null) ?? null,
      model: (row.model as string | null) ?? null,
      category: (row.category as string | null) ?? null,
      rawText,
      descriptionSnapshot: String(row.description ?? row.name ?? ""),
      imageUrlSnapshot: null,
      quantity: Math.max(1, Math.round(Number(row.quantity ?? 1))),
      unitPriceSnapshot: Math.max(0, Number(row.unitPrice ?? 0)),
      lineTotal: Math.max(0, Number(row.totalPrice ?? 0)),
      sortOrder: sortOrder++,
    });
  }
  return items;
}

/** Rebuild minimal line items from hierarchy snapshot when DB rows were lost. */
export function recoverDtoolsProposalDbItemsFromHierarchySnapshot(
  parsedMetadataJson: unknown,
): ProposalDbItemForDtoolsView[] {
  const meta = asRecord(parsedMetadataJson);
  const snapshot = asRecord(meta?.dtoolsHierarchySnapshot);
  const lines = snapshot?.lines;
  if (!Array.isArray(lines)) return [];

  return lines
    .map((entry, idx) => {
      const row = asRecord(entry);
      if (!row) return null;
      const dtoolsItemId = String(row.dtoolsItemId ?? row.id ?? "").trim();
      if (!dtoolsItemId) return null;
      const rawText = JSON.stringify({
        source: DTOOLS_JSON_SOURCE,
        dtoolsItemId,
        catalogueKey: row.catalogueKey ?? "",
        catalogId: row.catalogId ?? null,
        componentId: row.componentId ?? null,
        bucket: row.bucket ?? "commercial",
        systemId: row.systemId ?? null,
        systemName: row.systemName ?? null,
        locationId: row.locationId ?? null,
        roomName: row.roomName ?? null,
        locationName: row.locationName ?? null,
        locationFullName: row.locationFullName ?? null,
        parentLocationId: row.parentLocationId ?? null,
        parentId: row.parentId ?? null,
        isAccessory: Boolean(row.isAccessory),
      });
      return {
        id: -(idx + 1),
        name: dtoolsItemId,
        rawText,
        descriptionSnapshot: dtoolsItemId,
        quantity: 1,
        unitPriceSnapshot: 0,
        lineTotal: 0,
        sortOrder: Number(row.sortOrder ?? idx),
      } satisfies ProposalDbItemForDtoolsView;
    })
    .filter((row): row is ProposalDbItemForDtoolsView => row != null);
}

export function resolveDtoolsProposalItemsForLiveView(
  header: ProposalHeaderForDtoolsView,
  dbItems: ProposalDbItemForDtoolsView[],
): ProposalDbItemForDtoolsView[] {
  const persisted = dbItems.filter(
    (item) => isDtoolsPersistedProposalItem(item) && !isProposalSummaryWrapperItem(item),
  );
  if (persisted.length > 0) return persisted;

  const fromAudit = recoverDtoolsProposalDbItemsFromParsedMetadata(header.parsedMetadataJson);
  if (fromAudit.length > 0) return fromAudit;

  const fromSnapshot = recoverDtoolsProposalDbItemsFromHierarchySnapshot(header.parsedMetadataJson);
  if (fromSnapshot.length > 0) return fromSnapshot;

  return dbItems.filter((item) => !isProposalSummaryWrapperItem(item));
}

export function shouldRenderDtoolsBeautifiedProposalView(
  header: ProposalHeaderForDtoolsView,
  items?: ProposalDbItemForDtoolsView[],
): boolean {
  if (!isDtoolsJsonProposalSourceValue(header.proposalSource)) return false;
  if (hasDtoolsHierarchySnapshot(header.parsedMetadataJson)) return true;
  if (hasDtoolsCommercialParseAudit(header.parsedMetadataJson)) return true;
  if (items && items.length > 0 && hasDtoolsPersistedProposalItems(items)) return true;
  return false;
}

export function resolveProposalContentLayout(
  header: ProposalHeaderForDtoolsView,
  items?: ProposalDbItemForDtoolsView[],
): ProposalContentLayout {
  return shouldRenderDtoolsBeautifiedProposalView(header, items) ? "dtools_beautified" : "standard";
}

export type DtoolsLiveProposalViewModel = {
  systems: DtoolsReviewSystem[];
  footer: DtoolsReviewFooter;
  parseResult: DtoolsJsonParseResult;
  paymentTerms: DtoolsPortalPaymentTerm[];
  displaySettings: ReturnType<typeof readDtoolsDisplaySettings>;
  systemContentMap: Record<string, import("./dtoolsProposalContent").DtoolsSystemContent>;
  presentation: DtoolsProposalPresentation;
  headerPresentation: ProposalPresentationHeaderInput;
  tabModelVersion: DtoolsTabModelVersion;
  systemClassifications: DtoolsSystemClassificationsMap;
  /** Present when approved VOs were applied at render time (read-only composition). */
  variationOrderScope?: ComposedProjectScope | null;
  /** System overview annotations from approved VOs (by system content key). */
  systemOverviewAnnotations?: Record<
    string,
    import("./variationOrderSystemOverview").VoSystemOverviewAnnotation
  >;
};

/** Beautified layout was intended but live hierarchy could not be rebuilt (e.g. missing line data). */
export function isDtoolsBeautifiedViewUnavailable(
  layout: ProposalContentLayout,
  model: DtoolsLiveProposalViewModel | null,
): boolean {
  return layout === "dtools_beautified" && model == null;
}

export type { ProposalPresentationHeaderInput };

export type BuildDtoolsLiveProposalViewOptions = {
  /** Approved VO current-scope — applied read-time; does not mutate proposal_items. */
  variationOrderScope?: ComposedProjectScope | null;
  systemOverviewAnnotations?: Record<
    string,
    import("./variationOrderSystemOverview").VoSystemOverviewAnnotation
  >;
};

export function buildDtoolsLiveProposalViewModel(
  header: ProposalHeaderForDtoolsView,
  items: ProposalDbItemForDtoolsView[],
  options?: BuildDtoolsLiveProposalViewOptions,
): DtoolsLiveProposalViewModel | null {
  if (!shouldRenderDtoolsBeautifiedProposalView(header, items)) return null;
  const resolvedItems = resolveDtoolsProposalItemsForLiveView(header, items);
  const overrides = readDtoolsHierarchyOverrides(header.parsedMetadataJson);
  const rawStagingLines = buildDtoolsStagingLinesFromProposalItems(resolvedItems, header.parsedMetadataJson);
  let stagingLines = applyHierarchyOverridesToLines(
    elevateServiceLinesToRoomPrimaries(sanitizeStagingLineFields(rawStagingLines), overrides),
    overrides,
  );
  const variationOrderScope = options?.variationOrderScope ?? null;
  if (variationOrderScope) {
    stagingLines = applyComposedScopeToStagingLines(stagingLines, variationOrderScope);
  }
  if (stagingLines.length === 0) return null;
  const hierarchy = groupIdenticalProductsInReviewHierarchy(buildReviewHierarchy(stagingLines));
  const partialParse = buildDtoolsParseResultFromProposalHeader(header);
  const liveLinesCustomerTotal = computeStagingLinesCustomerTotal(stagingLines);
  const headerTotalForRecon =
    variationOrderScope != null
      ? variationOrderScope.revisedContractValue
      : partialParse.reconciliation.headerTotal;
  const reconciliation = {
    ...partialParse.reconciliation,
    linesCustomerTotal: liveLinesCustomerTotal,
    headerTotal: headerTotalForRecon,
    deltaVsHeaderTotal: roundMoney(
      liveLinesCustomerTotal - (headerTotalForRecon ?? liveLinesCustomerTotal),
    ),
  };
  const parseResult: DtoolsJsonParseResult = {
    version: DTOOLS_JSON_IMPORT_VERSION,
    source: DTOOLS_JSON_SOURCE,
    project: partialParse.project,
    customer: {
      clientName: String(header.customerNameSnapshot ?? "").trim() || null,
      clientId: null,
      siteAddress: null,
      siteAddressParts: null,
    },
    paymentTerms: partialParse.paymentTerms,
    reconciliation,
    warnings: [],
    locations: [],
    items: [],
    excludedLaborItems: [],
    grouped: {
      systems: [],
      adjustments: [],
      optionalItems: [],
      nonBillableItems: [],
      discounts: [],
      services: [],
      cables: [],
      unassigned: [],
    },
    uploadLineItems: [],
    itemCount: stagingLines.length,
    rawMetadata: asRecord(header.parsedMetadataJson) ?? {},
  };

  const systemContentMap = readDtoolsSystemContentMap(header.parsedMetadataJson);
  const presentation = readDtoolsProposalPresentation(header.parsedMetadataJson);

  const displaySettings = withInternalSystemsHiddenByDefault(
    hierarchy.systems,
    readDtoolsDisplaySettings(header.parsedMetadataJson),
  );
  const tabGate = resolveDtoolsCustomerTabGate({
    systems: hierarchy.systems,
    parsedMetadataJson: header.parsedMetadataJson,
  });

  return {
    systems: hierarchy.systems,
    footer: hierarchy.footer,
    parseResult,
    paymentTerms: resolveCustomerFacingPaymentTerms({
      paymentTermsJson: header.paymentTermsJson,
      displaySettings,
    }),
    displaySettings,
    systemContentMap,
    presentation,
    headerPresentation: {
      proposalName: header.proposalName,
      proposalRef: header.proposalRef,
      customerNameSnapshot: header.customerNameSnapshot ?? null,
      projectNumber: header.projectNumber ?? null,
      linkBExpiresAt: header.linkBExpiresAt ?? null,
    },
    tabModelVersion: tabGate.tabModelVersion,
    systemClassifications: tabGate.systemClassifications,
    variationOrderScope,
    systemOverviewAnnotations: options?.systemOverviewAnnotations ?? {},
  };
}
